import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styles from './Edit.module.css';
import formData from './PartnerAccountDetailsFormData.json';
import Form from '../../../Foundation/Helpers/Components/Forms/Form';
import CustomLoader from '../../../Foundation/Helpers/Components/Feature/Common/MicroUi/CustomLoader/CustomLoader';
import EditDetailsPanel from '../../Details/EditDetailsPanel/EditDetailsPanel';
import { HTTP_STATUS_CODES } from '../../../Foundation/Rest/Constants';
import { flattenDropdownOption, mapFormViewModel, mapNameIdPairToSelectOptions } from '../../../Foundation/Helpers/Components/Forms/Utility/formHelpers';
import _adminPartnerAccountServicesApiService from '../../../Application/ApiServices/Partner/AdminPartnerServices/Services/PartnerAccountServicesApiService';
import GenericError from '../../../Foundation/Helpers/Components/Feature/Common/Error/GenericError';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import Notification from '../../../Foundation/Helpers/Components/Feature/Common/Notification/Notification';
import { getCounties } from '../../../Foundation/Store/Actions/Utility/utilityActions';

import teeTimesData from '../TeeTimes.json';
import { SlotTimesFieldKeys } from '../settings';

const COUNTY_ID_KEY = 'CountyId',
  PARTNER_IMAGE_URIS_KEY = 'PartnerImages',
  DATA_API_PROPERTIES_KEY = 'AccountDetails';

const TEE_TIMES = [...teeTimesData.am, ...teeTimesData.pm];

const Details = () => {

  const auth = useSelector((state) => state.authentication);
  const counties = useSelector((state) => state.utility.counties);
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [validationErrorData, setValidationErrorData] = useState(null);
  const [isPendingData, setIsPendingData] = useState(false);
  const [isPendingPartnerData, setIsPendingPartnerData] = useState(false);
  const [countiesList, setCountiesList] = useState(null);
  const [partnerDataApiResponse, setPartnerDataApiResponse] = useState(null);

  // remote form controls (outside of component) and state
  const remoteEditToggle = useRef();
  const remoteCancelEdit = useRef();
  const remoteSubmit = useRef();
  const [isEditMode, setIsEditMode] = useState(false)
  const routeParams = useParams();
  const accountNumber = routeParams.id;

  const buildFormViewModel = (apiData) => {

    let result = mapFormViewModel(formData, apiData, DATA_API_PROPERTIES_KEY);

    // counties
    for (let section in result.formViewModel.sections) {

      for (let field of result.formViewModel.sections[section].fields) {
        if (apiData[DATA_API_PROPERTIES_KEY]?.hasOwnProperty(field.name)) {
          let entity = apiData[DATA_API_PROPERTIES_KEY][field.name];
          if (field.name === COUNTY_ID_KEY) {
            let counties = mapNameIdPairToSelectOptions(countiesList);
            field.options = counties;
            field.displayValue = counties.find(x => x.value === entity)?.label;
          }
        }

        if (field.name === PARTNER_IMAGE_URIS_KEY) {
          field.defaultValue = field.displayValue = apiData[DATA_API_PROPERTIES_KEY].PartnerImageUris !== null ? apiData[DATA_API_PROPERTIES_KEY].PartnerImageUris : [];
        }

        if (Object.values(SlotTimesFieldKeys).includes(field.name)) {
          field.options = TEE_TIMES;
        }
      }
    }

    return result;
  }

  const submitUpdate = (submitData) => {

    if (typeof auth.accessToken !== 'undefined') {

      setIsPendingData(true);

      _adminPartnerAccountServicesApiService.update(submitData, accountNumber).then((response) => {

        // server validation error
        if (response.apiStatus === HTTP_STATUS_CODES.BadRequest) {

          let sentDataThatErrored = { ...data, ...response }
          flattenDropdownOption(sentDataThatErrored, submitData, DATA_API_PROPERTIES_KEY, COUNTY_ID_KEY);
          setValidationErrorData(response.errors);
          setData(buildFormViewModel(sentDataThatErrored));
          toast(<Notification label="There were some errors" mode="error" />)

        } else {

          // success
          setValidationErrorData(null);
          setData(buildFormViewModel(response));
          setPartnerDataApiResponse(response)
          toast(<Notification label="Course details updated" mode="success" />)
        }

        setIsPendingData(false);
      })
    }
  }

  useEffect(() => {

    // only when both of these two have returned (async), can the form be built
    if (partnerDataApiResponse !== null && partnerDataApiResponse?.apiStatus === HTTP_STATUS_CODES.Success && countiesList !== null) {
      setData(buildFormViewModel(partnerDataApiResponse));
    }

    if (partnerDataApiResponse !== null && countiesList !== null) {
      setIsPendingData(false);
    }

  }, [partnerDataApiResponse, countiesList]);

  useEffect(() => {

    if (typeof auth?.accessToken !== 'undefined') {
      setIsPendingData(true);

      if (counties === null) {
        dispatch(getCounties());
      } else {
        setCountiesList(counties);
      }

      if (!isPendingPartnerData) {
        setIsPendingPartnerData(true);

        _adminPartnerAccountServicesApiService.get(accountNumber).then((response) => {
          setPartnerDataApiResponse(response);
          setIsPendingPartnerData(false);
        });
      }

    }
  }, [counties]);

  formData.onSubmit = submitUpdate;

  return <div className={`${styles.edit} layout__sub-section`}>
    <div className={`${styles.panel} layout__side-panel`}>
      {partnerDataApiResponse?.apiStatus === HTTP_STATUS_CODES.Success &&
        <EditDetailsPanel isEditMode={isEditMode} editRef={remoteEditToggle} cancelRef={remoteCancelEdit} submitRef={remoteSubmit} submitLabel={formData.submit?.name} isPendingEdit={isPendingData}>
          <h3>Manage Course Details</h3>
        </EditDetailsPanel>
      }
    </div>
    <div className={`layout__editor-panel ${styles.editor}`}>
      {isPendingData ? <CustomLoader label={"Loading"} pending={isPendingData} className={styles.loader} /> :
        partnerDataApiResponse?.apiStatus === HTTP_STATUS_CODES.Success ? <Form model={formData} editable={true} submissionResponseData={validationErrorData} remoteEditToggle={remoteEditToggle}
          remoteSubmit={remoteSubmit} remoteCancelEdit={remoteCancelEdit} remoteStateSetter={setIsEditMode} hideControls />
          : <GenericError title={"Error"} code={partnerDataApiResponse?.apiStatus}></GenericError>}
    </div>
  </div>

};

export default Details;

Details.propTypes = {

}
